/*
 * ************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *   Copyright 2020 Adobe Systems Incorporated
 *   All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 * ************************************************************************
 */

import * as R from 'ramda';
import classnames from 'classnames';
import React from 'react';
import { defineMessages, injectIntl } from '@nebula/izzy/intl';
import {
  ActionButton,
  Flex,
  Heading,
  Item,
  Picker,
  Text,
  TextArea,
  Tooltip,
  TooltipTrigger
} from '@adobe/react-spectrum';
import InfoOutline from '@spectrum-icons/workflow/InfoOutline';
import styles from './styles/settings.validation.styl';

const MSG = defineMessages({
  addArgumentsPlaceholder: {
    id: 'gemini.settings.plugin.validation.addArgumentsPlaceholder',
    defaultMessage: 'Add arguments...'
  },
  labelLevel: {
    id: 'gemini.settings.plugin.validation.label.level',
    defaultMessage: 'Level'
  },
  levelOff: {
    id: 'gemini.settings.plugin.validation.level.off',
    defaultMessage: 'Off'
  },
  levelWarn: {
    id: 'gemini.settings.plugin.validation.level.warn',
    defaultMessage: 'Warn'
  },
  levelError: {
    id: 'gemini.settings.plugin.validation.level.error',
    defaultMessage: 'Error'
  }
});

const LEVEL_MESSAGES = {
  error: 'levelError',
  warn: 'levelWarn',
  off: 'levelOff'
};

const SettingsValidation = ({
  editable = false,
  izzy,
  onChangeArguments,
  onSelectLevel,
  plugins,
  settings = {}
}) => {
  const levelOptions = R.pipe(
    R.keys,
    R.map(key => ({ name: izzy(MSG[LEVEL_MESSAGES[key]]), id: key }))
  )(LEVEL_MESSAGES);

  return (
    <div
      className={styles.root}
    >
      <div
        className={classnames(
          styles.root,
          (!plugins || !plugins.length) && styles.noViewPlugins
        )}
      >
        {R.map((categoryPlugins) => {
          const category = R.path([0, 'category'], categoryPlugins);

          return (
            <div key={category}>
              <Heading level={3}>{category}</Heading>
              {R.map(({ description, displayName, namespace, orgId }) => {
                const validationArgs = R.pathOr('', [namespace, 'arguments'], settings);
                const validationLevel = R.has(namespace, settings) ? R.path([namespace, 'level'], settings)
                  : settings.orgId === orgId ? 'error' : 'off';

                return (
                  <div className={styles.validationItem} key={namespace}>
                    <Flex alignItems="center" direction="row" justifyContent="space-between">
                      <Flex alignItems="center" direction="row">
                        <Text>{displayName}</Text>
                        {description && (
                          <TooltipTrigger
                            delay={0}
                          >
                            <ActionButton isQuiet>
                              <InfoOutline />
                            </ActionButton>
                            <Tooltip>{description}</Tooltip>
                          </TooltipTrigger>
                        )}
                      </Flex>
                      <Flex
                        alignItems="center"
                        direction="row"
                        gap="16px"
                        justifyContent="space-between"
                      >
                        <Picker
                          aria-label={izzy(MSG.labelLevel)}
                          isDisabled={!editable}
                          isQuiet
                          items={levelOptions}
                          menuWidth="size-1200"
                          onSelectionChange={v => onSelectLevel(namespace, v)}
                          selectedKey={validationLevel || 'error'}
                        >
                          {item => <Item key={item.id}>{item.name}</Item>}
                        </Picker>
                        <TextArea
                          aria-label={izzy(MSG.addArgumentsPlaceholder)}
                          description={izzy(MSG.addArgumentsPlaceholder)}
                          isDisabled={!editable || validationLevel === 'off'}
                          isQuiet
                          onChange={v => onChangeArguments(namespace, v)}
                          value={validationArgs}
                        />
                      </Flex>
                    </Flex>
                  </div>
                );
              }, categoryPlugins)}
            </div>
          );
        }, R.values(plugins))}
      </div>
    </div>
  );
};

SettingsValidation.displayName = 'SettingsValidation';
export default injectIntl(SettingsValidation);
